import { createContext, useContext, useMemo, type ReactNode } from 'react';

export type AppNavPresentation = 'docked' | 'drawer';

export interface AppNavPresentationValue {
  presentation: AppNavPresentation;
  /** Dismisses the drawer. Only set while `presentation` is `drawer`. */
  onClose?: () => void;
}

const AppNavPresentationContext = createContext<AppNavPresentationValue>({
  presentation: 'docked',
});

/**
 * Tells a navigation component how its host presents it. `AppLayout` provides
 * `drawer` on narrow viewports so the side nav can swap its collapse toggle
 * for a close button, without the nav deciding what a narrow viewport is.
 */
export function AppNavPresentationProvider({
  children,
  onClose,
  presentation,
}: {
  children: ReactNode;
  presentation: AppNavPresentation;
  onClose?: () => void;
}) {
  const value = useMemo(
    () => ({ onClose, presentation }),
    [onClose, presentation],
  );

  return (
    <AppNavPresentationContext.Provider value={value}>
      {children}
    </AppNavPresentationContext.Provider>
  );
}

export function useAppNavPresentation(): AppNavPresentationValue {
  return useContext(AppNavPresentationContext);
}
